import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { AlertsService } from "src/app/core/alerts.service";
import { ApiError } from "src/app/core/api-error";
import { Permission, UserService } from "src/app/core/auth";
import { ButtonComponent } from "src/app/core/elements/button.component";
import { ModalComponent } from "src/app/core/elements/modal.component";
import { Modal } from "src/app/core/modal.service";
import { SitesService } from "src/app/core/sites";
import { AppointmentReference } from "src/app/partner/appointments/base-appointment.model";
import { HelpAssistActionsService } from "src/app/partner/help-assist/help-assist-actions.service";
import { HelpAssistHasScheduledTicketForRequest } from "src/app/partner/help-assist/models/help-assist-has-schedule-ticket-for-request.model";
import {
  getHelpAssistTicketRouteUrl,
  HelpAssistTicketReference,
} from "src/app/partner/help-assist/models/ticket/base-help-assist-ticket.model";
import { HelpAssistTicketAppointmentDraftUpdate } from "src/app/partner/help-assist/models/ticket-appointment-draft/help-assist-ticket-appointment-draft-update.model";
import { TicketAppointmentDetailsComponent } from "src/app/partner/help-assist/ticket-appointment-details/ticket-appointment-details.component";
import { environment } from "src/environments";
import { parseNumber } from "src/utils";
import { HelpAssistGeneralTicketFormComponent } from "./general-ticket-form.component";
import { HelpAssistSchedulingTicketFormComponent } from "./scheduling-ticket-form.component";
import { TicketPoListComponent } from "./ticket-po-list.component";

type ViewType = "mainView" | "generalTicketView" | "appointmentTicketView";

export interface TicketModalContext {
  appointment?: HelpAssistTicketAppointmentDraftUpdate;
  appointmentId?: AppointmentReference["id"];
  areGeneralTicketsEnabled?: boolean;
  areSchedulingTicketsEnabled?: boolean;
  isAppointmentCreation?: boolean;
  orderSearchTerms?: readonly string[];
  view?: ViewType;
}

@Component({
  selector: "mr-help-assist-modal[display]",
  templateUrl: "./help-assist-modal.component.html",
  styleUrls: ["./help-assist-modal.component.scss"],
  providers: [Modal],
  imports: [
    ModalComponent,
    ButtonComponent,
    HelpAssistGeneralTicketFormComponent,
    TicketAppointmentDetailsComponent,
    TicketPoListComponent,
    HelpAssistSchedulingTicketFormComponent,
  ],
})
export class HelpAssistModalComponent {
  public constructor(
    public readonly modal: Modal<
      TicketModalContext,
      "createdGeneralTicket" | "createdAppointmentTicket" | "cancelled"
    >,
    private readonly router: Router,
    private readonly user: UserService,
    private readonly alerts: AlertsService,
    private readonly helpAssistActions: HelpAssistActionsService,
    private readonly sites: SitesService,
  ) {}

  public readonly modalView: {
    AppointmentView: ViewType;
    GeneralView: ViewType;
    MainView: ViewType;
  } = {
    AppointmentView: "appointmentTicketView",
    GeneralView: "generalTicketView",
    MainView: "mainView",
  };

  public currentView: ViewType =
    this.modal.context.view || this.modalView.GeneralView;

  public readonly appointment = this.modal.context.appointment ?? null;

  public readonly appointmentId = this.modal.context.appointmentId ?? null;

  public readonly isAppointmentCreation =
    this.modal.context.isAppointmentCreation ?? false;

  public readonly orderSearchTerms = this.modal.context.orderSearchTerms;

  public readonly areSchedulingTicketsEnabled =
    this.modal.context.areSchedulingTicketsEnabled ?? false;

  public readonly areGeneralTicketsEnabled =
    this.modal.context.areGeneralTicketsEnabled ?? false;

  public readonly helpAssistAskUsPhoneNumber =
    environment.helpAssist.askUsPhoneNumber;

  // TODO: Can this be consolidated into just ViewHelpDeskTicketDetails?
  // Presumably, the "admin" permission here is for extended information, so
  // it should always extend the base details permission.
  private readonly hasTicketViewPermission =
    this.user.permissions.has(Permission.ViewHelpDeskAdminDetails) ||
    this.user.permissions.has(Permission.ViewHelpDeskTicketDetails);

  public readonly hasOrders =
    this.appointment !== null &&
    this.appointment.orders !== null &&
    this.appointment.orders.length > 0;

  public async openTicket(type: ViewType): Promise<void> {
    try {
      if (type !== this.modalView.GeneralView) {
        const site = await firstValueFrom(this.sites.selectedChanges);
        const request = new HelpAssistHasScheduledTicketForRequest({
          appointmentDraft: this.appointment,
          appointmentId: this.appointmentId,
          site,
        });

        const hasExistingTicket =
          await this.helpAssistActions.navigateToExistingTicketIfFound(request);

        if (hasExistingTicket) {
          this.modal.close();
          return;
        }
      }

      this.currentView = type;
    } catch (error) {
      await this.handleSubmitError(error);
    }
  }

  private async navigateToDetailsPage(
    ticket: HelpAssistTicketReference,
  ): Promise<void> {
    if (!this.hasTicketViewPermission) {
      return;
    }

    await this.router.navigateByUrl(getHelpAssistTicketRouteUrl(ticket));
  }

  public async close(): Promise<void> {
    await this.modal.choose("cancelled");
  }

  public async handleAppointmentSubmitSuccess(
    ticket: HelpAssistTicketReference,
  ): Promise<void> {
    this.alerts.success({ title: "Saved Successfully!" });
    await this.modal.choose("createdAppointmentTicket");
    await this.navigateToDetailsPage(ticket);
  }

  public async handleGenericSubmitSuccess(
    ticket: HelpAssistTicketReference,
  ): Promise<void> {
    this.alerts.success({ title: "Saved Successfully!" });
    await this.modal.choose("createdGeneralTicket");
    await this.navigateToDetailsPage(ticket);
  }

  public async handleSubmitError(error: unknown): Promise<void> {
    if (
      error instanceof ApiError &&
      error.apiCode === "OPEN_TICKET_EXISTS" &&
      error.apiTarget
    ) {
      this.alerts.warn({
        title: "An active Help Assist Ticket already exists.",
        message: "You will be redirected to the existing Help Assist ticket.",
      });

      this.modal.close();

      const preExistingTicketId = parseNumber(error.apiTarget);
      if (preExistingTicketId === null) {
        throw new Error(
          `Could not parse API target as number: ${error.apiTarget}`,
        );
      }
      const site = await firstValueFrom(this.sites.selectedChanges);
      await this.navigateToDetailsPage({ id: preExistingTicketId, site });
    } else {
      this.alerts.error({
        title: "Failed to Save",
        message: "Your ticket was not created. Please try again.",
        error,
      });
    }
  }
}
